import axios, { AxiosRequestConfig } from 'axios';
import type { EcritureAEnregistrer, MetaNote, MetaNoteResume, PlateauAEnregistrer } from '../types/metanote';

const createConfig = (accessToken: string): AxiosRequestConfig => ({
    headers: { 'Authorization': `Bearer ${accessToken}`, 'AccessToken': accessToken, 'Content-Type': 'application/json' },
});

const BASE = '/api/metanotes';

/**
 * Client de `metanotes` (chantier C28 SP1, spec § 7) : mes plateaux, propriétaire seul.
 * Les erreurs remontent à l'appelant ; le slice lit le statut (409 = conflit de version).
 */
export const metanoteService = {
    lister: async (accessToken: string): Promise<MetaNoteResume[]> => (await axios.get(BASE, createConfig(accessToken))).data.data,
    lire: async (id: string, accessToken: string): Promise<MetaNote> => (await axios.get(`${BASE}/${encodeURIComponent(id)}`, createConfig(accessToken))).data.data,
    creer: async (titre: string, accessToken: string): Promise<MetaNote> => (await axios.post(BASE, { titre }, createConfig(accessToken))).data.data,
    enregistrer: async (id: string, plateau: PlateauAEnregistrer, accessToken: string): Promise<MetaNote> =>
        (await axios.put(`${BASE}/${encodeURIComponent(id)}`, plateau, createConfig(accessToken))).data.data,
    supprimer: async (id: string, accessToken: string): Promise<void> => { await axios.delete(`${BASE}/${encodeURIComponent(id)}`, createConfig(accessToken)); },
    /** L'écriture de la note (C28 SP2) : `POST …/{id}/ecriture`, versionnée (409 = conflit, relayé au slice). */
    enregistrerEcriture: async (id: string, ecriture: EcritureAEnregistrer, accessToken: string): Promise<MetaNote> =>
        (await axios.post(`${BASE}/${encodeURIComponent(id)}/ecriture`, ecriture, createConfig(accessToken))).data.data,
    /** La note écrite n'existe plus : `DELETE …/{id}/ecriture?version=N`. */
    oublierEcriture: async (id: string, version: number, accessToken: string): Promise<MetaNote> =>
        (await axios.delete(`${BASE}/${encodeURIComponent(id)}/ecriture?version=${encodeURIComponent(String(version))}`, createConfig(accessToken))).data.data,
    parNote: async (noteId: string, accessToken: string): Promise<MetaNote | null> => {
        try {
            return (await axios.get(`${BASE}?noteId=${encodeURIComponent(noteId)}`, createConfig(accessToken))).data.data;
        } catch (error: any) {
            if (error?.response?.status === 404) return null;
            throw error;
        }
    },
};

export default metanoteService;
